import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Actions} from '../../../common/actions';
import {Card, Tab} from 'semantic-ui-react';
import {
  validateFieldsRequiredInForm, validateFieldsRequiredInGeneralInformation,
  processFiles,
} from './utils';
import {getSystemParameterValue} from '../../../common/util/parametersUtils';
import {resources, scopes} from '../../../common/permissions/dictionary';
import {hasAccessPermission} from '../../../common/permissions/validations';
import {paths} from '../../../common/dictionaries/urls';
import TemplateFormButtons from './buttons';
import GeneralInformation from './tabs/generalInformation/generalInformation';
import FormTemplate from './tabs/form/form';
import Attachments from './tabs/attachments/attachments';
import Record from './tabs/record/Record';
import Glossary from './tabs/glossary/glossary';
import './create.scss';

const PANES_INDICES = {
  'GENERAL_INFORMATION': 0,
  'FORM': 1,
  'ATTACHMENTS': 2,
  'GLOSSARY': 3,
  'HISTORY': 4,
};

const panes = [
  {'menuItem': 'Información General'},
  {'menuItem': 'Formulario'},
  {'menuItem': 'Adjuntos'},
  {'menuItem': 'Definiciones'},
  {'menuItem': 'Historial'},
];

let timer = null;

class TemplateForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      'activeTabIndex': 0,
      'intervalTimeForAutoSave': 300,
    };
    this.changeTabIndex = this.changeTabIndex.bind(this);
    this.onSaveForm = this.onSaveForm.bind(this);
    this.onUpdateForm = this.onUpdateForm.bind(this);
  }

  componentDidMount() {
    if (!hasAccessPermission(this.props.permissions, resources.FormsManage, scopes.Create)) {
      this.props.push(paths.AccessDenied);
    }
    this.props.fetchSystemParameter('INTERVAL_AUTO_SAVE_FORMS');
    this.getFormAndResponseData();
  }

  startTimer() {
    const {actionType} = this.props;
    const {intervalTimeForAutoSave} = this.state;

    timer = setInterval(() => {
      switch (actionType) {
        default:
        case 'editar':
          return this.onUpdateForm(null, true);
        case 'nuevo':
        case 'clonar':
          return this.onSaveForm('in_progress', true);
      }
    }, intervalTimeForAutoSave * 1000);
  }

  stopTimer() {
    if (timer) {
      clearInterval(timer);
    }
  }

  getFormAndResponseData() {
    const {actionType, id, answerId, dealerId, user} = this.props;
    this.props.clearForm();

    if (user) {
      this.props.setGeneralInfo('user', user);
      this.props.setGeneralInfo('responsible', user.toUpperCase());
    }
    if (dealerId) {
      this.props.fetchDealer(dealerId);
      this.props.fetchAgenciesByDealer({dealerId});
    }

    switch (actionType) {
      default:
      case 'ver':
        this.props.fetchFormResponse(id, actionType);
        this.stopTimer();
        break;
      case 'editar':
        this.props.fetchFormResponse(id, actionType);
        this.stopTimer();
        this.startTimer();
        break;
      case 'nuevo':
        this.props.fetchFormTemplate(id);
        this.stopTimer();
        this.startTimer();
        break;
      case 'clonar':
        this.props.fetchFormTemplate(id, answerId);
        this.stopTimer();
        this.startTimer();
        break;
      case 'clonar-todo':
        this.props.fetchFormResponse(answerId, actionType);
        this.stopTimer();
        this.startTimer();
        break;
    }
  }

  manageRedirections(prevProps) {
    const {actionType, formOrResponseId, saved, autosave, deleted} = this.props;

    if (saved && prevProps.saved !== saved) {
      this.props.changeSavedValue(false);
      if (autosave) {
        this.props.push(`/formulario/${formOrResponseId}/editar`);
      } else if (!autosave && actionType !== 'ver') {
        this.props.push(`/formulario/${formOrResponseId}/ver`);
        this.setState({'activeTabIndex': 0});
      }
      return;
    }

    if (deleted && prevProps.deleted !== deleted) {
      this.props.push(paths.EnteredAnswersList);
    }
  }

  componentDidUpdate(prevProps) {
    const {actionType, systemParameters} = this.props;
    const {intervalTimeForAutoSave} = this.state;
    
    if (prevProps.systemParameters !== systemParameters) {
      const intervalToSaveInfo = getSystemParameterValue(systemParameters, 'INTERVAL_AUTO_SAVE_FORMS');
      if (intervalToSaveInfo && intervalToSaveInfo !== intervalTimeForAutoSave) {
        this.setState({'intervalTimeForAutoSave': intervalToSaveInfo}, () => this.getFormAndResponseData());
      }
    }

    if (prevProps.actionType !== actionType) {
      this.getFormAndResponseData();
      if (actionType === 'ver' || actionType === 'editar') {
        this.setState({'activeTabIndex': 0});
      }
      return;
    }

    this.manageRedirections(prevProps);
  }

  componentWillUnmount() {
    this.stopTimer();
  }

  onSaveForm(status, autosave) {
    const {id, formName, section, generalInfo, answers, sections, formType, attachments} = this.props;

    const formData = processFiles(attachments);
    formData.append('formId', id);
    formData.append('type', formType);
    formData.append('formName', formName);
    formData.append('section', section);
    formData.append('status', status);
    formData.append('generalInfo', JSON.stringify(generalInfo));
    formData.append('answers', JSON.stringify(answers));
    formData.append('sections', JSON.stringify(sections));

    this.props.saveForm(formData, autosave);
  }

  onUpdateForm(status, autosave) {
    const {id, generalInfo, answers, attachments} = this.props;

    const formData = processFiles(attachments);
    formData.append('generalInfo', JSON.stringify(generalInfo));
    formData.append('answers', JSON.stringify(answers));
    if (status) {
      formData.append('status', status);
    }

    this.props.updateForm(id, formData, autosave);
  }

  isFieldsRequiredFilled(currentTab) {
    const {actionType, generalInfo, answers} = this.props;

    switch (currentTab) {
      case PANES_INDICES.GENERAL_INFORMATION:
        return validateFieldsRequiredInGeneralInformation(actionType, generalInfo);
      case PANES_INDICES.FORM:
        return validateFieldsRequiredInForm(actionType, answers);
      default:
        return false;
    }
  }

  changeTabIndex(event, data) {
    const currentTab = data.defaultActiveIndex;
    const isFieldsRequiredFilledError = this.isFieldsRequiredFilled(currentTab);
    if (isFieldsRequiredFilledError) {
      this.props.addMessageBoxError(isFieldsRequiredFilledError);
      return;
    }

    this.setState({'activeTabIndex': data.activeIndex});
  }

  renderPanesContent() {
    const {activeTabIndex} = this.state;
    const {actionType, type, status} = this.props;
    const isDisplayMode = actionType === 'ver' || status === 'published';
    
    switch (activeTabIndex) {
      default:
      case PANES_INDICES.GENERAL_INFORMATION:
        return <GeneralInformation isDisplayMode={isDisplayMode} />;
      case PANES_INDICES.FORM:
        return <FormTemplate isDisplayMode={isDisplayMode} {...this.props} />;
      case PANES_INDICES.ATTACHMENTS:
        return <Attachments isDisplayMode={type === 'RSR' && actionType !== 'ver' ? false : isDisplayMode} />;
      case PANES_INDICES.GLOSSARY:
        return <Glossary />;
      case PANES_INDICES.HISTORY: {
        if (type === 'RSR') {
          return <Record/>;
        }
        return null;
      }
    }
  }

  render() {
    const {formName, type, id, actionType, formType} = this.props;
    const {activeTabIndex} = this.state;
    const panesWithOutRecord = [...panes];
    panesWithOutRecord.pop();
    const panesFilter = type === 'RSR' ? [...panes] : panesWithOutRecord;
    return (
      <div className='icc-form'>
        <Card raised fluid>
          <div className='title-bar'>
            <div className='title'>
              <h2>Formulario: {formName}</h2>
              <TemplateFormButtons
                id={id}
                actionType={actionType}
                formType={formType}
                onUpdateForm={this.onUpdateForm}
                onSaveForm={this.onSaveForm} />
            </div>
          </div>

          <Tab
            menu={{'secondary': true, 'pointing': true, 'color': 'red'}}
            panes={panesFilter}
            defaultActiveIndex={activeTabIndex}
            onTabChange={this.changeTabIndex}
          />
        </Card>

        {this.renderPanesContent()}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const {id, actionType, answerId, formType} = ownProps.params;
  return {
    id,
    actionType,
    answerId,
    formType,
    'formOrResponseId': state.templateFTR.id,
    'dealerId': state.session.dealerId,
    'user': state.session.user,
    'saved': state.templateFTR.saved,
    'autosave': state.templateFTR.autosave,
    'formName': state.templateFTR.name,
    'section': state.templateFTR.section,
    'type': state.templateFTR.type,
    'status': state.templateFTR.status,
    'generalInfo': state.templateFTR.generalInfo,
    'answers': state.templateFTR.answers,
    'sections': state.templateFTR.sections,
    'attachments': state.templateFTR.attachments,
    'deleted': state.templateFTR.deleted,
    'systemParameters': state.parameters.list,
    'menuSelected': state.menu.selected,
    'permissions': state.session.permissions,
    'push': ownProps.history.push,
  };
};

const {
  withRouter,
  fetchSystemParameter,
  clearForm,
  setGeneralInfo,
  fetchDealer,
  fetchAgenciesByDealer,
  fetchFormTemplate,
  saveForm,
  updateForm,
  fetchFormResponse,
  addMessageBoxError,
  changeSavedValue,
} = Actions;

export default withRouter(connect(mapStateToProps, {
  fetchSystemParameter,
  clearForm,
  setGeneralInfo,
  fetchDealer,
  fetchAgenciesByDealer,
  fetchFormTemplate,
  saveForm,
  updateForm,
  fetchFormResponse,
  addMessageBoxError,
  changeSavedValue,
})(TemplateForm));
